import { useState, useEffect, useRef } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { Document, Page, pdfjs } from 'react-pdf';
import { useParams, useNavigate } from 'react-router-dom';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function MagazineViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth * 0.95,
    height: ((window.innerWidth * 0.95) / 2) * 1.4,
    pageWidth: (window.innerWidth * 0.95) / 2,
  });
  const flipBookRef = useRef();

  useEffect(() => {
    function handleResize() {
      const totalWidth = window.innerWidth * 0.95;
      setDimensions({
        width: totalWidth,
        height: (totalWidth / 2) * 1.4,
        pageWidth: totalWidth / 2,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial calculation

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mock magazine data
  const magazine = {
    id: parseInt(id),
    title: 'Winter 2024',
    publishedAt: '2024-01-01',
    url: '/assets/magazines/sample.pdf',
  };

  // PDF document handlers
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setIsLoading(false);
  }

  function onDocumentLoadError(error) {
    setIsLoading(false);
    setError('Failed to load the magazine. Please try again later.');
    // eslint-disable-next-line no-console
    console.error('PDF load error:', error);
  }

  // Navigation handlers
  function goToPrevPage() {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flipPrev();
    }
  }

  function goToNextPage() {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flipNext();
    }
  }

  return (
    <div className="min-h-screen bg-hdlcc-darkGray">
      <div>
        <main className="w-full">
          <div className="text-white">
            {/* Header */}
            <header className="px-2 py-3 border-b border-hdlcc-gray">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold">{magazine.title}</h1>
                  <p className="text-gray-400">
                    Published{' '}
                    {new Date(magazine.publishedAt).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/magazine')}
                  className="px-4 py-2 text-white bg-hdlcc-gray rounded hover:bg-hdlcc-darkGray"
                >
                  Back to Magazines
                </button>
              </div>
            </header>

            {/* Content */}
            <div className="px-2">
              {error ? (
                <div className="flex justify-center items-center h-96">
                  <div className="text-center">
                    <p className="text-red-500 text-lg mb-4">{error}</p>
                    <button
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">
                  {/* Magazine Viewer */}
                  <div className="w-full max-w-4xl mx-auto">
                    {isLoading && (
                      <div className="flex justify-center items-center h-96">
                        <p className="text-lg">Loading magazine...</p>
                      </div>
                    )}

                    <Document
                      file={magazine.url}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={null}
                    >
                      <div className="w-full flex flex-col items-center relative">
                        <div className="flex justify-center w-full">
                          <HTMLFlipBook
                            key={dimensions.width}
                            ref={flipBookRef}
                            width={dimensions.pageWidth}
                            height={dimensions.height}
                            size="fixed"
                            minWidth={300}
                            maxWidth={1000}
                            minHeight={400}
                            showCover={true}
                            className="magazine-viewer"
                            style={{ padding: 0, marginBottom: 0 }}
                            drawShadow={false}
                            usePortrait={false}
                            startZIndex={0}
                            flippingTime={1000}
                            onFlip={e => {
                              const currentPage = e.data;
                              setPageNumber(currentPage === 0 ? 1 : currentPage + 1);
                            }}
                          >
                            {Array.from(new Array(numPages), (el, index) => (
                              <div
                                key={`page_${index + 1}`}
                                className="page"
                                style={{ padding: 0 }}
                              >
                                <Page
                                  pageNumber={index + 1}
                                  width={dimensions.pageWidth}
                                  height={dimensions.height}
                                  renderTextLayer={false}
                                  renderAnnotationLayer={false}
                                  className="max-w-full"
                                />
                              </div>
                            ))}
                          </HTMLFlipBook>
                        </div>

                        {/* Navigation Controls */}
                        {numPages && (
                          <div
                            className="flex items-center justify-center space-x-4 bg-hdlcc-darkGray px-4 py-1 rounded-full shadow-lg absolute"
                            style={{
                              marginTop: '-1px',
                              top: dimensions.height,
                              left: '50%',
                              transform: 'translateX(-50%)',
                            }}
                          >
                            <button
                              onClick={goToPrevPage}
                              disabled={pageNumber <= 1}
                              className="px-3 py-1 text-sm font-medium text-white bg-hdlcc-gray border border-hdlcc-gray rounded-md hover:bg-hdlcc-darkGray disabled:opacity-50"
                            >
                              Previous
                            </button>
                            <span className="text-sm text-white">
                              {pageNumber === 1
                                ? 'Page 1'
                                : pageNumber === numPages && numPages % 2 === 0
                                  ? `Page ${pageNumber}`
                                  : `Pages ${pageNumber}-${Math.min(pageNumber + 1, numPages)}`}{' '}
                              of {numPages}
                            </span>
                            <button
                              onClick={goToNextPage}
                              disabled={pageNumber >= numPages}
                              className="px-3 py-1 text-sm font-medium text-white bg-hdlcc-gray border border-hdlcc-gray rounded-md hover:bg-hdlcc-darkGray disabled:opacity-50"
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </div>
                    </Document>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default MagazineViewer;
